import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { RutField, fillEmpty, lookupValues } from '../components/RutField';
import { toast } from 'sonner';
import {
  Shield, ArrowLeft, ArrowRight, Loader2, CheckCircle2, FileText,
  AlertTriangle, Download, Eye, Save, Sparkles, User, Building2,
  Calendar, MapPin, DollarSign, Hash, Mail, Phone, FileCheck,
  Clock, Info, ChevronDown, ChevronUp
} from 'lucide-react';

// Field type icons mapping
const fieldTypeIcons = {
  text: FileText,
  nombre: User,
  rut: Hash,
  fecha: Calendar,
  direccion: MapPin,
  monto: DollarSign,
  email: Mail,
  telefono: Phone,
  empresa: Building2,
  default: FileText
};

// Common field definitions for Chilean legal documents
const TEMPLATE_FIELDS = {
  // Finiquito Laboral
  finiquito_laboral: {
    title: "Finiquito Laboral",
    description: "Complete los datos para generar el finiquito de término de relación laboral",
    sections: [
      {
        name: "Datos del Empleador",
        fields: [
          { id: "empleador_nombre", label: "Nombre o Razón Social", type: "text", required: true, placeholder: "Empresa S.A." },
          { id: "empleador_rut", label: "RUT Empleador", type: "rut", required: true, placeholder: "76.123.456-7" },
          { id: "empleador_representante", label: "Representante Legal", type: "text", required: true, placeholder: "Juan Pérez González" },
          { id: "empleador_direccion", label: "Dirección", type: "text", required: true, placeholder: "Av. Principal 123, Santiago" }
        ]
      },
      {
        name: "Datos del Trabajador",
        fields: [
          { id: "trabajador_nombre", label: "Nombre Completo", type: "text", required: true, placeholder: "María García López" },
          { id: "trabajador_rut", label: "RUT Trabajador", type: "rut", required: true, placeholder: "12.345.678-9" },
          { id: "trabajador_direccion", label: "Dirección", type: "text", required: false, placeholder: "Calle 456, Providencia" },
          { id: "trabajador_cargo", label: "Cargo", type: "text", required: true, placeholder: "Analista de Sistemas" }
        ]
      },
      {
        name: "Datos del Contrato",
        fields: [
          { id: "fecha_inicio", label: "Fecha de Inicio del Contrato", type: "date", required: true },
          { id: "fecha_termino", label: "Fecha de Término", type: "date", required: true },
          { id: "causal_termino", label: "Causal de Término", type: "select", required: true, options: [
            { value: "mutuo_acuerdo", label: "Mutuo acuerdo de las partes (Art. 159 N°1)" },
            { value: "renuncia", label: "Renuncia del trabajador (Art. 159 N°2)" },
            { value: "vencimiento_plazo", label: "Vencimiento del plazo (Art. 159 N°4)" },
            { value: "conclusion_obra", label: "Conclusión del trabajo o servicio (Art. 159 N°5)" },
            { value: "necesidades_empresa", label: "Necesidades de la empresa (Art. 161)" },
            { value: "desahucio", label: "Desahucio escrito del empleador (Art. 161)" }
          ]},
          { id: "sueldo_base", label: "Último Sueldo Base ($)", type: "number", required: true, placeholder: "1500000" }
        ]
      },
      {
        name: "Montos a Pagar",
        fields: [
          { id: "dias_trabajados", label: "Días trabajados mes actual", type: "number", required: false, placeholder: "15" },
          { id: "vacaciones_pendientes", label: "Días de vacaciones pendientes", type: "number", required: false, placeholder: "10" },
          { id: "indemnizacion_años", label: "Años para indemnización", type: "number", required: false, placeholder: "3" },
          { id: "otros_pagos", label: "Otros pagos ($)", type: "number", required: false, placeholder: "0" },
          { id: "descuentos", label: "Descuentos ($)", type: "number", required: false, placeholder: "0" }
        ]
      }
    ]
  },
  
  // Contrato de Arriendo
  contrato_arriendo: {
    title: "Contrato de Arriendo",
    description: "Complete los datos para generar el contrato de arrendamiento",
    sections: [
      {
        name: "Datos del Arrendador",
        fields: [
          { id: "arrendador_nombre", label: "Nombre Completo", type: "text", required: true, placeholder: "Juan Pérez González" },
          { id: "arrendador_rut", label: "RUT", type: "rut", required: true, placeholder: "12.345.678-9" },
          { id: "arrendador_direccion", label: "Dirección", type: "text", required: true, placeholder: "Av. Principal 123" },
          { id: "arrendador_email", label: "Email", type: "email", required: false, placeholder: "correo@ejemplo.com" }
        ]
      },
      {
        name: "Datos del Arrendatario",
        fields: [
          { id: "arrendatario_nombre", label: "Nombre Completo", type: "text", required: true, placeholder: "María García López" },
          { id: "arrendatario_rut", label: "RUT", type: "rut", required: true, placeholder: "98.765.432-1" },
          { id: "arrendatario_direccion", label: "Dirección Actual", type: "text", required: false, placeholder: "Calle 456" },
          { id: "arrendatario_email", label: "Email", type: "email", required: false, placeholder: "correo@ejemplo.com" }
        ]
      },
      {
        name: "Datos de la Propiedad",
        fields: [
          { id: "propiedad_direccion", label: "Dirección de la Propiedad", type: "text", required: true, placeholder: "Calle Los Aromos 789, Depto 501" },
          { id: "propiedad_comuna", label: "Comuna", type: "text", required: true, placeholder: "Providencia" },
          { id: "propiedad_ciudad", label: "Ciudad", type: "text", required: true, placeholder: "Santiago" },
          { id: "propiedad_tipo", label: "Tipo de Propiedad", type: "select", required: true, options: [
            { value: "departamento", label: "Departamento" },
            { value: "casa", label: "Casa" },
            { value: "oficina", label: "Oficina" },
            { value: "local_comercial", label: "Local Comercial" },
            { value: "bodega", label: "Bodega" }
          ]}
        ]
      },
      {
        name: "Condiciones del Arriendo",
        fields: [
          { id: "monto_arriendo", label: "Monto Mensual ($)", type: "number", required: true, placeholder: "500000" },
          { id: "dia_pago", label: "Día de Pago", type: "number", required: true, placeholder: "5" },
          { id: "garantia", label: "Meses de Garantía", type: "number", required: true, placeholder: "1" },
          { id: "fecha_inicio", label: "Fecha de Inicio", type: "date", required: true },
          { id: "duracion_meses", label: "Duración (meses)", type: "number", required: true, placeholder: "12" },
          { id: "gastos_comunes", label: "¿Incluye gastos comunes?", type: "select", required: true, options: [
            { value: "si", label: "Sí, incluidos en el arriendo" },
            { value: "no", label: "No, pago separado por arrendatario" }
          ]}
        ]
      }
    ]
  },

  // Compraventa de Vehículo
  compraventa_vehiculo: {
    title: "Compraventa de Vehículo",
    description: "Complete los datos para generar el contrato de compraventa de vehículo",
    sections: [
      {
        name: "Datos del Vendedor",
        fields: [
          { id: "vendedor_nombre", label: "Nombre Completo", type: "text", required: true },
          { id: "vendedor_rut", label: "RUT", type: "rut", required: true },
          { id: "vendedor_direccion", label: "Dirección", type: "text", required: true }
        ]
      },
      {
        name: "Datos del Comprador",
        fields: [
          { id: "comprador_nombre", label: "Nombre Completo", type: "text", required: true },
          { id: "comprador_rut", label: "RUT", type: "rut", required: true },
          { id: "comprador_direccion", label: "Dirección", type: "text", required: true }
        ]
      },
      {
        name: "Datos del Vehículo",
        fields: [
          { id: "vehiculo_marca", label: "Marca", type: "text", required: true, placeholder: "Toyota" },
          { id: "vehiculo_modelo", label: "Modelo", type: "text", required: true, placeholder: "Corolla" },
          { id: "vehiculo_año", label: "Año", type: "number", required: true, placeholder: "2020" },
          { id: "vehiculo_patente", label: "Patente", type: "text", required: true, placeholder: "ABCD-12" },
          { id: "vehiculo_motor", label: "N° Motor", type: "text", required: true },
          { id: "vehiculo_chasis", label: "N° Chasis", type: "text", required: true },
          { id: "vehiculo_color", label: "Color", type: "text", required: true },
          { id: "vehiculo_kilometraje", label: "Kilometraje", type: "number", required: false }
        ]
      },
      {
        name: "Condiciones de Venta",
        fields: [
          { id: "precio_venta", label: "Precio de Venta ($)", type: "number", required: true },
          { id: "forma_pago", label: "Forma de Pago", type: "select", required: true, options: [
            { value: "contado", label: "Contado" },
            { value: "transferencia", label: "Transferencia Bancaria" },
            { value: "cheque", label: "Cheque" },
            { value: "cuotas", label: "En Cuotas" }
          ]},
          { id: "fecha_entrega", label: "Fecha de Entrega", type: "date", required: true }
        ]
      }
    ]
  },

  // Poder Simple
  poder_simple: {
    title: "Poder Simple",
    description: "Complete los datos para generar el poder de representación",
    sections: [
      {
        name: "Datos del Poderdante (quien otorga el poder)",
        fields: [
          { id: "poderdante_nombre", label: "Nombre Completo", type: "text", required: true, placeholder: "Juan Pérez González" },
          { id: "poderdante_rut", label: "RUT", type: "rut", required: true, placeholder: "12.345.678-9" },
          { id: "poderdante_nacionalidad", label: "Nacionalidad", type: "text", required: true, placeholder: "Chilena" },
          { id: "poderdante_estado_civil", label: "Estado Civil", type: "select", required: true, options: [
            { value: "soltero", label: "Soltero/a" },
            { value: "casado", label: "Casado/a" },
            { value: "divorciado", label: "Divorciado/a" },
            { value: "viudo", label: "Viudo/a" }
          ]},
          { id: "poderdante_profesion", label: "Profesión u Oficio", type: "text", required: false, placeholder: "Ingeniero" },
          { id: "poderdante_direccion", label: "Domicilio", type: "text", required: true, placeholder: "Av. Principal 123, Santiago" }
        ]
      },
      {
        name: "Datos del Apoderado (quien recibe el poder)",
        fields: [
          { id: "apoderado_nombre", label: "Nombre Completo", type: "text", required: true, placeholder: "María García López" },
          { id: "apoderado_rut", label: "RUT", type: "rut", required: true, placeholder: "98.765.432-1" },
          { id: "apoderado_profesion", label: "Profesión u Oficio", type: "text", required: false, placeholder: "Abogada" },
          { id: "apoderado_direccion", label: "Domicilio", type: "text", required: true, placeholder: "Calle 456, Providencia" }
        ]
      },
      {
        name: "Facultades del Poder",
        fields: [
          { id: "tipo_poder", label: "Tipo de Poder", type: "select", required: true, options: [
            { value: "general_administracion", label: "General de Administración" },
            { value: "especial", label: "Especial (acto específico)" },
            { value: "amplio", label: "Amplio (incluye disposición)" },
            { value: "judicial", label: "Judicial (representación en juicios)" }
          ]},
          { id: "facultades_especificas", label: "Facultades Específicas", type: "textarea", required: true, placeholder: "Describa las facultades que otorga: cobrar, percibir, demandar, etc." },
          { id: "vigencia", label: "Vigencia", type: "select", required: true, options: [
            { value: "indefinido", label: "Indefinido" },
            { value: "1_año", label: "1 año" },
            { value: "2_años", label: "2 años" },
            { value: "hasta_revocacion", label: "Hasta su revocación" }
          ]}
        ]
      }
    ]
  },

  // Mandato
  mandato: {
    title: "Contrato de Mandato",
    description: "Complete los datos para generar el contrato de mandato",
    sections: [
      {
        name: "Datos del Mandante",
        fields: [
          { id: "mandante_nombre", label: "Nombre Completo", type: "text", required: true },
          { id: "mandante_rut", label: "RUT", type: "rut", required: true },
          { id: "mandante_direccion", label: "Domicilio", type: "text", required: true }
        ]
      },
      {
        name: "Datos del Mandatario",
        fields: [
          { id: "mandatario_nombre", label: "Nombre Completo", type: "text", required: true },
          { id: "mandatario_rut", label: "RUT", type: "rut", required: true },
          { id: "mandatario_direccion", label: "Domicilio", type: "text", required: true }
        ]
      },
      {
        name: "Objeto del Mandato",
        fields: [
          { id: "objeto_mandato", label: "Descripción del Encargo", type: "textarea", required: true, placeholder: "Describa el negocio o gestión encomendada" },
          { id: "es_remunerado", label: "¿Es remunerado?", type: "select", required: true, options: [
            { value: "si", label: "Sí" },
            { value: "no", label: "No (gratuito)" }
          ]},
          { id: "remuneracion", label: "Remuneración ($)", type: "number", required: false, placeholder: "0" },
          { id: "plazo", label: "Plazo del Mandato", type: "text", required: false, placeholder: "Ej: 6 meses, hasta conclusión del negocio" }
        ]
      }
    ]
  },

  // Promesa de Compraventa
  promesa_compraventa: {
    title: "Promesa de Compraventa",
    description: "Complete los datos para generar la promesa de compraventa de inmueble",
    sections: [
      {
        name: "Datos del Promitente Vendedor",
        fields: [
          { id: "vendedor_nombre", label: "Nombre Completo", type: "text", required: true },
          { id: "vendedor_rut", label: "RUT", type: "rut", required: true },
          { id: "vendedor_estado_civil", label: "Estado Civil", type: "select", required: true, options: [
            { value: "soltero", label: "Soltero/a" },
            { value: "casado", label: "Casado/a" },
            { value: "divorciado", label: "Divorciado/a" },
            { value: "viudo", label: "Viudo/a" }
          ]},
          { id: "vendedor_direccion", label: "Domicilio", type: "text", required: true }
        ]
      },
      {
        name: "Datos del Promitente Comprador",
        fields: [
          { id: "comprador_nombre", label: "Nombre Completo", type: "text", required: true },
          { id: "comprador_rut", label: "RUT", type: "rut", required: true },
          { id: "comprador_estado_civil", label: "Estado Civil", type: "select", required: true, options: [
            { value: "soltero", label: "Soltero/a" },
            { value: "casado", label: "Casado/a" },
            { value: "divorciado", label: "Divorciado/a" },
            { value: "viudo", label: "Viudo/a" }
          ]},
          { id: "comprador_direccion", label: "Domicilio", type: "text", required: true }
        ]
      },
      {
        name: "Datos del Inmueble",
        fields: [
          { id: "inmueble_direccion", label: "Dirección del Inmueble", type: "text", required: true },
          { id: "inmueble_comuna", label: "Comuna", type: "text", required: true },
          { id: "inmueble_rol", label: "Rol de Avalúo", type: "text", required: false, placeholder: "123-456" },
          { id: "inmueble_superficie", label: "Superficie (m²)", type: "number", required: false },
          { id: "inmueble_inscripcion", label: "Inscripción de Dominio", type: "text", required: false, placeholder: "Foja X N° X año X" }
        ]
      },
      {
        name: "Condiciones de la Promesa",
        fields: [
          { id: "precio_venta", label: "Precio de Venta ($)", type: "number", required: true },
          { id: "arras", label: "Monto de Arras ($)", type: "number", required: false, placeholder: "10% del precio" },
          { id: "plazo_escritura", label: "Plazo para Escritura Definitiva", type: "text", required: true, placeholder: "Ej: 60 días" },
          { id: "notaria", label: "Notaría para Escritura", type: "text", required: false },
          { id: "multa_incumplimiento", label: "Multa por Incumplimiento ($)", type: "number", required: false }
        ]
      }
    ]
  },

  // Cesión de Derechos
  cesion_derechos: {
    title: "Cesión de Derechos",
    description: "Complete los datos para generar el contrato de cesión de derechos",
    sections: [
      {
        name: "Datos del Cedente",
        fields: [
          { id: "cedente_nombre", label: "Nombre Completo", type: "text", required: true },
          { id: "cedente_rut", label: "RUT", type: "rut", required: true },
          { id: "cedente_direccion", label: "Domicilio", type: "text", required: true }
        ]
      },
      {
        name: "Datos del Cesionario",
        fields: [
          { id: "cesionario_nombre", label: "Nombre Completo", type: "text", required: true },
          { id: "cesionario_rut", label: "RUT", type: "rut", required: true },
          { id: "cesionario_direccion", label: "Domicilio", type: "text", required: true }
        ]
      },
      {
        name: "Derechos Cedidos",
        fields: [
          { id: "tipo_derecho", label: "Tipo de Derecho", type: "select", required: true, options: [
            { value: "credito", label: "Crédito" },
            { value: "herencia", label: "Derechos Hereditarios" },
            { value: "acciones", label: "Acciones o Derechos Sociales" },
            { value: "contractual", label: "Derechos Contractuales" },
            { value: "otro", label: "Otro" }
          ]},
          { id: "descripcion_derecho", label: "Descripción del Derecho", type: "textarea", required: true, placeholder: "Describa detalladamente los derechos cedidos" },
          { id: "precio_cesion", label: "Precio de la Cesión ($)", type: "number", required: true },
          { id: "forma_pago", label: "Forma de Pago", type: "text", required: true, placeholder: "Ej: Al contado, cuotas, etc." }
        ]
      }
    ]
  },

  // Contrato de Mutuo (Préstamo)
  mutuo: {
    title: "Contrato de Mutuo (Préstamo)",
    description: "Complete los datos para generar el contrato de préstamo de dinero",
    sections: [
      {
        name: "Datos del Mutuante (quien presta)",
        fields: [
          { id: "mutuante_nombre", label: "Nombre Completo", type: "text", required: true },
          { id: "mutuante_rut", label: "RUT", type: "rut", required: true },
          { id: "mutuante_direccion", label: "Domicilio", type: "text", required: true }
        ]
      },
      {
        name: "Datos del Mutuario (quien recibe)",
        fields: [
          { id: "mutuario_nombre", label: "Nombre Completo", type: "text", required: true },
          { id: "mutuario_rut", label: "RUT", type: "rut", required: true },
          { id: "mutuario_direccion", label: "Domicilio", type: "text", required: true }
        ]
      },
      {
        name: "Condiciones del Préstamo",
        fields: [
          { id: "monto_prestamo", label: "Monto del Préstamo ($)", type: "number", required: true },
          { id: "tiene_interes", label: "¿Devenga interés?", type: "select", required: true, options: [
            { value: "si", label: "Sí" },
            { value: "no", label: "No (gratuito)" }
          ]},
          { id: "tasa_interes", label: "Tasa de Interés Mensual (%)", type: "number", required: false, placeholder: "Ej: 1.5" },
          { id: "plazo_devolucion", label: "Plazo de Devolución", type: "text", required: true, placeholder: "Ej: 12 meses" },
          { id: "forma_pago", label: "Forma de Pago", type: "select", required: true, options: [
            { value: "cuota_fija", label: "Cuotas fijas mensuales" },
            { value: "al_vencimiento", label: "Capital al vencimiento" },
            { value: "cuotas_variables", label: "Cuotas variables" }
          ]},
          { id: "garantia", label: "Garantía (si aplica)", type: "text", required: false, placeholder: "Ej: Prenda, hipoteca, aval" }
        ]
      }
    ]
  },

  // Comodato (Préstamo de uso)
  comodato: {
    title: "Contrato de Comodato",
    description: "Complete los datos para generar el contrato de préstamo de uso gratuito",
    sections: [
      {
        name: "Datos del Comodante (quien presta)",
        fields: [
          { id: "comodante_nombre", label: "Nombre Completo", type: "text", required: true },
          { id: "comodante_rut", label: "RUT", type: "rut", required: true },
          { id: "comodante_direccion", label: "Domicilio", type: "text", required: true }
        ]
      },
      {
        name: "Datos del Comodatario (quien recibe)",
        fields: [
          { id: "comodatario_nombre", label: "Nombre Completo", type: "text", required: true },
          { id: "comodatario_rut", label: "RUT", type: "rut", required: true },
          { id: "comodatario_direccion", label: "Domicilio", type: "text", required: true }
        ]
      },
      {
        name: "Objeto del Comodato",
        fields: [
          { id: "tipo_bien", label: "Tipo de Bien", type: "select", required: true, options: [
            { value: "inmueble", label: "Inmueble (casa, depto, local)" },
            { value: "vehiculo", label: "Vehículo" },
            { value: "maquinaria", label: "Maquinaria/Equipo" },
            { value: "otro", label: "Otro bien" }
          ]},
          { id: "descripcion_bien", label: "Descripción del Bien", type: "textarea", required: true, placeholder: "Describa el bien prestado" },
          { id: "uso_autorizado", label: "Uso Autorizado", type: "text", required: true, placeholder: "Ej: Habitación, oficina, transporte" },
          { id: "plazo", label: "Plazo del Comodato", type: "text", required: true, placeholder: "Ej: 1 año, indefinido" },
          { id: "fecha_entrega", label: "Fecha de Entrega", type: "date", required: true }
        ]
      }
    ]
  },

  // Recibo de Dinero
  recibo_dinero: {
    title: "Recibo de Dinero",
    description: "Complete los datos para generar un recibo de dinero",
    sections: [
      {
        name: "Datos del Receptor (quien recibe el dinero)",
        fields: [
          { id: "receptor_nombre", label: "Nombre Completo", type: "text", required: true, placeholder: "Juan Pérez González" },
          { id: "receptor_rut", label: "RUT", type: "rut", required: true, placeholder: "12.345.678-9" },
          { id: "receptor_direccion", label: "Domicilio", type: "text", required: false, placeholder: "Av. Principal 123, Santiago" }
        ]
      },
      {
        name: "Datos del Pagador (quien entrega el dinero)",
        fields: [
          { id: "pagador_nombre", label: "Nombre Completo", type: "text", required: true, placeholder: "María García López" },
          { id: "pagador_rut", label: "RUT", type: "rut", required: true, placeholder: "98.765.432-1" },
          { id: "pagador_direccion", label: "Domicilio", type: "text", required: false, placeholder: "Calle 456, Providencia" }
        ]
      },
      {
        name: "Detalles del Pago",
        fields: [
          { id: "monto", label: "Monto Recibido ($)", type: "number", required: true, placeholder: "500000" },
          { id: "monto_palabras", label: "Monto en Palabras", type: "text", required: true, placeholder: "Quinientos mil pesos" },
          { id: "concepto", label: "Concepto del Pago", type: "textarea", required: true, placeholder: "Pago por servicios de consultoría, arriendo mes de enero, etc." },
          { id: "forma_pago", label: "Forma de Pago", type: "select", required: true, options: [
            { value: "efectivo", label: "Efectivo" },
            { value: "transferencia", label: "Transferencia Bancaria" },
            { value: "cheque", label: "Cheque" },
            { value: "vale_vista", label: "Vale Vista" }
          ]},
          { id: "numero_documento", label: "N° Cheque/Transferencia (si aplica)", type: "text", required: false },
          { id: "fecha_pago", label: "Fecha del Pago", type: "date", required: true },
          { id: "ciudad", label: "Ciudad", type: "text", required: true, placeholder: "Santiago" }
        ]
      }
    ]
  },

  // Declaración Jurada
  declaracion_jurada: {
    title: "Declaración Jurada",
    description: "Complete los datos para generar una declaración jurada",
    sections: [
      {
        name: "Datos del Declarante",
        fields: [
          { id: "declarante_nombre", label: "Nombre Completo", type: "text", required: true, placeholder: "Juan Pérez González" },
          { id: "declarante_rut", label: "RUT", type: "rut", required: true, placeholder: "12.345.678-9" },
          { id: "declarante_nacionalidad", label: "Nacionalidad", type: "text", required: true, placeholder: "Chilena" },
          { id: "declarante_profesion", label: "Profesión u Oficio", type: "text", required: false, placeholder: "Ingeniero" },
          { id: "declarante_estado_civil", label: "Estado Civil", type: "select", required: true, options: [
            { value: "soltero", label: "Soltero/a" },
            { value: "casado", label: "Casado/a" },
            { value: "divorciado", label: "Divorciado/a" },
            { value: "viudo", label: "Viudo/a" }
          ]},
          { id: "declarante_direccion", label: "Domicilio", type: "text", required: true, placeholder: "Av. Principal 123, Santiago" }
        ]
      },
      {
        name: "Contenido de la Declaración",
        fields: [
          { id: "tipo_declaracion", label: "Tipo de Declaración", type: "select", required: true, options: [
            { value: "ingresos", label: "Declaración de Ingresos" },
            { value: "no_renta", label: "Declaración de No Percepción de Rentas" },
            { value: "solteria", label: "Declaración de Soltería" },
            { value: "convivencia", label: "Declaración de Convivencia" },
            { value: "dependencia", label: "Declaración de Dependencia Económica" },
            { value: "patrimonio", label: "Declaración de Patrimonio" },
            { value: "antecedentes", label: "Declaración de No Tener Antecedentes" },
            { value: "otro", label: "Otra Declaración" }
          ]},
          { id: "contenido_declaracion", label: "Declaración (texto completo)", type: "textarea", required: true, placeholder: "Declaro bajo juramento que... (describa los hechos que declara)" },
          { id: "proposito", label: "Propósito de la Declaración", type: "text", required: true, placeholder: "Ej: Trámite de visa, subsidio habitacional, etc." },
          { id: "institucion_destino", label: "Institución de Destino", type: "text", required: false, placeholder: "Ej: Serviu, Extranjería, etc." }
        ]
      },
      {
        name: "Datos Adicionales",
        fields: [
          { id: "ciudad", label: "Ciudad", type: "text", required: true, placeholder: "Santiago" },
          { id: "fecha", label: "Fecha de la Declaración", type: "date", required: true },
          { id: "testigo1_nombre", label: "Nombre Testigo 1 (opcional)", type: "text", required: false },
          { id: "testigo1_rut", label: "RUT Testigo 1", type: "rut", required: false },
          { id: "testigo2_nombre", label: "Nombre Testigo 2 (opcional)", type: "text", required: false },
          { id: "testigo2_rut", label: "RUT Testigo 2", type: "rut", required: false }
        ]
      }
    ]
  },

  // Declaración de Residencia
  declaracion_residencia: {
    title: "Declaración de Residencia",
    description: "Complete los datos para generar una declaración de residencia",
    sections: [
      {
        name: "Datos del Declarante (Propietario o Arrendatario)",
        fields: [
          { id: "declarante_nombre", label: "Nombre Completo", type: "text", required: true, placeholder: "Juan Pérez González" },
          { id: "declarante_rut", label: "RUT", type: "rut", required: true, placeholder: "12.345.678-9" },
          { id: "declarante_nacionalidad", label: "Nacionalidad", type: "text", required: true, placeholder: "Chilena" },
          { id: "declarante_calidad", label: "Calidad de Ocupación", type: "select", required: true, options: [
            { value: "propietario", label: "Propietario" },
            { value: "arrendatario", label: "Arrendatario" },
            { value: "usufructuario", label: "Usufructuario" },
            { value: "comodatario", label: "Comodatario" }
          ]}
        ]
      },
      {
        name: "Datos del Residente (persona que reside)",
        fields: [
          { id: "residente_nombre", label: "Nombre Completo del Residente", type: "text", required: true, placeholder: "María García López" },
          { id: "residente_rut", label: "RUT del Residente", type: "rut", required: true, placeholder: "98.765.432-1" },
          { id: "residente_nacionalidad", label: "Nacionalidad del Residente", type: "text", required: true, placeholder: "Chilena" },
          { id: "relacion", label: "Relación con el Declarante", type: "select", required: true, options: [
            { value: "hijo", label: "Hijo/a" },
            { value: "padre", label: "Padre/Madre" },
            { value: "conyuge", label: "Cónyuge" },
            { value: "conviviente", label: "Conviviente" },
            { value: "hermano", label: "Hermano/a" },
            { value: "sobrino", label: "Sobrino/a" },
            { value: "amigo", label: "Amigo/a" },
            { value: "arrendatario", label: "Arrendatario/a" },
            { value: "otro", label: "Otro" }
          ]}
        ]
      },
      {
        name: "Datos del Domicilio",
        fields: [
          { id: "direccion", label: "Dirección Completa", type: "text", required: true, placeholder: "Av. Principal 123, Depto 501" },
          { id: "comuna", label: "Comuna", type: "text", required: true, placeholder: "Providencia" },
          { id: "ciudad", label: "Ciudad", type: "text", required: true, placeholder: "Santiago" },
          { id: "region", label: "Región", type: "text", required: true, placeholder: "Metropolitana" },
          { id: "tipo_vivienda", label: "Tipo de Vivienda", type: "select", required: true, options: [
            { value: "casa", label: "Casa" },
            { value: "departamento", label: "Departamento" },
            { value: "pieza", label: "Pieza" },
            { value: "otro", label: "Otro" }
          ]}
        ]
      },
      {
        name: "Período de Residencia",
        fields: [
          { id: "fecha_inicio", label: "Fecha de Inicio de Residencia", type: "date", required: true },
          { id: "residencia_actual", label: "¿Es residencia actual?", type: "select", required: true, options: [
            { value: "si", label: "Sí, reside actualmente" },
            { value: "no", label: "No, ya no reside" }
          ]},
          { id: "fecha_termino", label: "Fecha de Término (si no es actual)", type: "date", required: false },
          { id: "proposito", label: "Propósito de la Declaración", type: "text", required: true, placeholder: "Ej: Trámite de visa, cédula de identidad, etc." },
          { id: "fecha_declaracion", label: "Fecha de la Declaración", type: "date", required: true }
        ]
      }
    ]
  },

  // Contrato de Trabajo
  contrato_trabajo: {
    title: "Contrato de Trabajo",
    description: "Complete los datos para generar el contrato de trabajo",
    sections: [
      {
        name: "Datos del Empleador",
        fields: [
          { id: "empleador_nombre", label: "Razón Social o Nombre", type: "text", required: true },
          { id: "empleador_rut", label: "RUT", type: "rut", required: true },
          { id: "empleador_representante", label: "Representante Legal", type: "text", required: true },
          { id: "empleador_direccion", label: "Dirección", type: "text", required: true },
          { id: "empleador_giro", label: "Giro o Actividad", type: "text", required: true }
        ]
      },
      {
        name: "Datos del Trabajador",
        fields: [
          { id: "trabajador_nombre", label: "Nombre Completo", type: "text", required: true },
          { id: "trabajador_rut", label: "RUT", type: "rut", required: true },
          { id: "trabajador_nacionalidad", label: "Nacionalidad", type: "text", required: true, placeholder: "Chilena" },
          { id: "trabajador_fecha_nacimiento", label: "Fecha de Nacimiento", type: "date", required: true },
          { id: "trabajador_estado_civil", label: "Estado Civil", type: "select", required: true, options: [
            { value: "soltero", label: "Soltero/a" },
            { value: "casado", label: "Casado/a" },
            { value: "divorciado", label: "Divorciado/a" },
            { value: "viudo", label: "Viudo/a" }
          ]},
          { id: "trabajador_direccion", label: "Domicilio", type: "text", required: true }
        ]
      },
      {
        name: "Condiciones del Trabajo",
        fields: [
          { id: "cargo", label: "Cargo o Función", type: "text", required: true },
          { id: "lugar_trabajo", label: "Lugar de Trabajo", type: "text", required: true },
          { id: "fecha_inicio", label: "Fecha de Inicio", type: "date", required: true },
          { id: "tipo_contrato", label: "Tipo de Contrato", type: "select", required: true, options: [
            { value: "indefinido", label: "Indefinido" },
            { value: "plazo_fijo", label: "Plazo Fijo" },
            { value: "obra_faena", label: "Por Obra o Faena" }
          ]},
          { id: "plazo_termino", label: "Fecha de Término (si aplica)", type: "date", required: false },
          { id: "jornada", label: "Jornada de Trabajo", type: "select", required: true, options: [
            { value: "completa", label: "Jornada Completa (45 hrs)" },
            { value: "parcial", label: "Jornada Parcial" },
            { value: "art22", label: "Art. 22 (sin límite de jornada)" }
          ]},
          { id: "horario", label: "Horario de Trabajo", type: "text", required: false, placeholder: "Ej: Lunes a Viernes 9:00 a 18:00" }
        ]
      },
      {
        name: "Remuneración",
        fields: [
          { id: "sueldo_base", label: "Sueldo Base ($)", type: "number", required: true },
          { id: "gratificacion", label: "Gratificación", type: "select", required: true, options: [
            { value: "art50", label: "Art. 50 (25% sueldo, tope 4.75 IMM)" },
            { value: "proporcional", label: "Proporcional a utilidades" }
          ]},
          { id: "colacion", label: "Colación ($)", type: "number", required: false },
          { id: "movilizacion", label: "Movilización ($)", type: "number", required: false },
          { id: "otros_beneficios", label: "Otros Beneficios", type: "text", required: false },
          { id: "forma_pago", label: "Forma de Pago", type: "select", required: true, options: [
            { value: "mensual", label: "Mensual" },
            { value: "quincenal", label: "Quincenal" },
            { value: "semanal", label: "Semanal" }
          ]}
        ]
      }
    ]
  },

  // Default template for unknown types
  default: {
    title: "Documento Legal",
    description: "Complete los datos para generar el documento",
    sections: [
      {
        name: "Datos de la Primera Parte",
        fields: [
          { id: "parte1_nombre", label: "Nombre Completo", type: "text", required: true },
          { id: "parte1_rut", label: "RUT", type: "rut", required: true },
          { id: "parte1_direccion", label: "Dirección", type: "text", required: false }
        ]
      },
      {
        name: "Datos de la Segunda Parte",
        fields: [
          { id: "parte2_nombre", label: "Nombre Completo", type: "text", required: true },
          { id: "parte2_rut", label: "RUT", type: "rut", required: true },
          { id: "parte2_direccion", label: "Dirección", type: "text", required: false }
        ]
      },
      {
        name: "Detalles del Documento",
        fields: [
          { id: "fecha", label: "Fecha", type: "date", required: true },
          { id: "descripcion", label: "Descripción", type: "textarea", required: false }
        ]
      }
    ]
  }
};

// More template mappings
const TEMPLATE_MAPPING = {
  'finiquito_laboral': 'finiquito_laboral',
  'finiquito': 'finiquito_laboral',
  'arriendo': 'contrato_arriendo',
  'contrato_arriendo': 'contrato_arriendo',
  'arrendamiento': 'contrato_arriendo',
  'arriendo_habitacional': 'contrato_arriendo',
  'arriendo_comercial': 'contrato_arriendo',
  'compraventa_vehiculo': 'compraventa_vehiculo',
  'vehiculo': 'compraventa_vehiculo',
  'auto': 'compraventa_vehiculo',
  'poder': 'poder_simple',
  'poder_simple': 'poder_simple',
  'poder_general': 'poder_simple',
  'poder_especial': 'poder_simple',
  'mandato': 'mandato',
  'promesa': 'promesa_compraventa',
  'promesa_compraventa': 'promesa_compraventa',
  'cesion': 'cesion_derechos',
  'cesion_derechos': 'cesion_derechos',
  'mutuo': 'mutuo',
  'prestamo': 'mutuo',
  'comodato': 'comodato',
  'contrato_trabajo': 'contrato_trabajo',
  'trabajo': 'contrato_trabajo',
  // Nuevas plantillas
  'recibo_dinero': 'recibo_dinero',
  'recibo': 'recibo_dinero',
  'declaracion_jurada': 'declaracion_jurada',
  'jurada': 'declaracion_jurada',
  'declaracion_residencia': 'declaracion_residencia',
  'residencia': 'declaracion_residencia',
  'certificado_residencia': 'declaracion_residencia'
};

const DirectTemplatePage = () => {
  const { templateId } = useParams();
  const { api, token } = useAuth();
  const navigate = useNavigate();
  
  const [template, setTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [errors, setErrors] = useState({});
  const [warnings, setWarnings] = useState({});
  const [suggestions, setSuggestions] = useState({});
  const [validatingField, setValidatingField] = useState(null);
  const [generatedContract, setGeneratedContract] = useState(null);

  // Get template definition
  const templateDef = useMemo(() => {
    const mappedId = TEMPLATE_MAPPING[templateId] || templateId;
    return TEMPLATE_FIELDS[mappedId] || TEMPLATE_FIELDS.default;
  }, [templateId]);

  useEffect(() => {
    fetchTemplateInfo();
    // Expand all sections by default
    const expanded = {};
    templateDef.sections.forEach((_, idx) => {
      expanded[idx] = true;
    });
    setExpandedSections(expanded);
  }, [templateId]);

  const fetchTemplateInfo = async () => {
    setLoading(true);
    try {
      // Try to get template info from API
      const response = await api.get(`/templates/${templateId}`);
      setTemplate(response.data);
    } catch (error) {
      // If API fails, use local definition
      setTemplate({
        id: templateId,
        name: templateDef.title,
        description: templateDef.description
      });
    }
    setLoading(false);
  };

  // AI-powered field validation
  const validateFieldWithAI = async (fieldId, value, fieldType) => {
    if (!value || !value.trim()) return;
    
    setValidatingField(fieldId);
    try {
      const response = await api.post('/templates/validate-field', {
        template_type: templateId,
        field_name: fieldId,
        field_value: value,
        field_type: fieldType || 'text'
      });
      
      const result = response.data;
      
      // Update errors
      if (result.errors && result.errors.length > 0) {
        setErrors(prev => ({ ...prev, [fieldId]: result.errors[0] }));
      } else {
        setErrors(prev => ({ ...prev, [fieldId]: null }));
      }
      
      // Update warnings
      if (result.warnings && result.warnings.length > 0) {
        setWarnings(prev => ({ ...prev, [fieldId]: result.warnings }));
      } else {
        setWarnings(prev => ({ ...prev, [fieldId]: null }));
      }
      
      // Update suggestions
      if (result.suggestions && result.suggestions.length > 0) {
        setSuggestions(prev => ({ ...prev, [fieldId]: result.suggestions }));
      } else {
        setSuggestions(prev => ({ ...prev, [fieldId]: null }));
      }
      
    } catch (error) {
      console.log('Validation API not available, using local validation');
    } finally {
      setValidatingField(null);
    }
  };

  const handleInputChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
    // Clear error when user types
    if (errors[fieldId]) {
      setErrors(prev => ({ ...prev, [fieldId]: null }));
    }
    // Clear warnings when user modifies
    if (warnings[fieldId]) {
      setWarnings(prev => ({ ...prev, [fieldId]: null }));
    }
  };

  // Handle field blur for AI validation
  const handleFieldBlur = (fieldId, value, fieldType) => {
    validateFieldWithAI(fieldId, value, fieldType);
  };

  const validateForm = () => {
    const newErrors = {};
    templateDef.sections.forEach(section => {
      section.fields.forEach(field => {
        if (field.required && !formData[field.id]?.trim()) {
          newErrors[field.id] = 'Este campo es requerido';
        }
        // Validate RUT format
        if (field.type === 'rut' && formData[field.id]) {
          const rutRegex = /^\d{1,2}\.\d{3}\.\d{3}-[\dkK]$/;
          if (!rutRegex.test(formData[field.id])) {
            newErrors[field.id] = 'Formato de RUT inválido (ej: 12.345.678-9)';
          }
        }
        // Validate email format
        if (field.type === 'email' && formData[field.id]) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(formData[field.id])) {
            newErrors[field.id] = 'Email inválido';
          }
        }
      });
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateContract = async () => {
    if (!validateForm()) {
      toast.error('Por favor complete todos los campos requeridos');
      return;
    }

    setGenerating(true);
    try {
      // Call the API to generate the contract (returns job_id for async generation)
      const response = await api.post('/contracts/generate-from-template', {
        template_id: templateId,
        data: formData
      });

      const jobId = response.data.job_id;
      
      if (jobId) {
        toast.info('Generando documento...');
        
        // Poll for job completion
        let attempts = 0;
        const maxAttempts = 30; // 30 seconds max
        
        const checkJob = async () => {
          attempts++;
          try {
            const jobResponse = await api.get(`/jobs/${jobId}`);
            const job = jobResponse.data;
            
            if (job.status === 'completed') {
              if (job.result?.contract_id) {
                toast.success('¡Contrato generado exitosamente!');
                navigate(`/contratos/${job.result.contract_id}`);
              } else {
                toast.success('¡Documento generado!');
                navigate('/contratos');
              }
              return;
            } else if (job.status === 'failed') {
              toast.error('Error al generar el documento');
              setGenerating(false);
              return;
            }
            
            // Still processing, continue polling
            if (attempts < maxAttempts) {
              setTimeout(checkJob, 1000);
            } else {
              toast.error('La generación está tardando demasiado. Revise sus contratos.');
              setGenerating(false);
            }
          } catch (err) {
            if (attempts < maxAttempts) {
              setTimeout(checkJob, 1000);
            } else {
              toast.error('Error al verificar el estado');
              setGenerating(false);
            }
          }
        };
        
        checkJob();
        return; // Don't set generating to false yet
      }
      
      // Direct response (no job)
      if (response.data.contract_id) {
        toast.success('¡Contrato generado exitosamente!');
        navigate(`/contratos/${response.data.contract_id}`);
      }
    } catch (error) {
      console.error('Error generating contract:', error);
      
      // Fallback: navigate to discovery with pre-filled data
      toast.error('Error al generar. Completando con asistente...');
      navigate(`/discovery/${templateId}`, { state: { prefilled: formData } });
    }
    setGenerating(false);
  };

  const toggleSection = (index) => {
    setExpandedSections(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const renderField = (field) => {
    const IconComponent = fieldTypeIcons[field.type] || fieldTypeIcons.default;
    const hasError = errors[field.id];
    const hasWarning = warnings[field.id] && warnings[field.id].length > 0;
    const hasSuggestion = suggestions[field.id] && suggestions[field.id].length > 0;
    const isValidating = validatingField === field.id;

    const baseInputClass = `w-full bg-slate-800 border rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 transition-all ${
      hasError ? 'border-red-500 focus:ring-red-500/50' : 
      hasWarning ? 'border-yellow-500 focus:ring-yellow-500/50' :
      'border-slate-700 focus:ring-blue-500/50 focus:border-blue-500'
    }`;

    const handleBlur = () => {
      handleFieldBlur(field.id, formData[field.id], field.type);
    };

    return (
      <div key={field.id} className="space-y-1.5">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-300">
          <IconComponent className="w-4 h-4 text-slate-500" />
          {field.label}
          {field.required && <span className="text-red-400">*</span>}
          {isValidating && <Loader2 className="w-3 h-3 text-blue-400 animate-spin ml-1" />}
        </label>
        
        {field.type === 'select' ? (
          <select
            value={formData[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            onBlur={handleBlur}
            className={baseInputClass}
            data-testid={`field-${field.id}`}
          >
            <option value="">Seleccione una opción</option>
            {field.options?.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        ) : field.type === 'textarea' ? (
          <textarea
            value={formData[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            onBlur={handleBlur}
            placeholder={field.placeholder}
            rows={3}
            className={baseInputClass}
            data-testid={`field-${field.id}`}
          />
        ) : field.type === 'date' ? (
          <input
            type="date"
            value={formData[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            onBlur={handleBlur}
            className={baseInputClass}
            data-testid={`field-${field.id}`}
          />
        ) : field.type === 'number' ? (
          <input
            type="number"
            value={formData[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            onBlur={handleBlur}
            placeholder={field.placeholder}
            className={baseInputClass}
            data-testid={`field-${field.id}`}
          />
        ) : field.type === 'rut' ? (
          <RutField
            testid={`field-${field.id}`}
            value={formData[field.id] || ''}
            onChange={(v) => handleInputChange(field.id, v)}
            onLookup={(d) => setFormData((prev) => fillEmpty(prev, lookupValues(field.id, templateDef.sections.flatMap((s) => s.fields.map((x) => x.id)), d)))}
            placeholder={field.placeholder}
            className={baseInputClass}
          />
        ) : (
          <input
            type={field.type === 'email' ? 'email' : 'text'}
            value={formData[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            onBlur={handleBlur}
            placeholder={field.placeholder}
            className={baseInputClass}
            data-testid={`field-${field.id}`}
          />
        )}
        
        {/* Error message */}
        {hasError && (
          <p className="text-xs text-red-400 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            {errors[field.id]}
          </p>
        )}
        
        {/* Warning messages */}
        {hasWarning && !hasError && (
          <div className="space-y-0.5">
            {warnings[field.id].map((warning, idx) => (
              <p key={idx} className="text-xs text-yellow-400 flex items-center gap-1">
                <Info className="w-3 h-3" />
                {warning}
              </p>
            ))}
          </div>
        )}
        
        {/* AI Suggestions */}
        {hasSuggestion && !hasError && (
          <div className="mt-1 p-2 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-xs text-blue-300 flex items-center gap-1 mb-1">
              <Sparkles className="w-3 h-3" />
              Sugerencia IA:
            </p>
            {suggestions[field.id].map((suggestion, idx) => (
              <p key={idx} className="text-xs text-blue-200 ml-4">• {suggestion}</p>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen mesh-gradient flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Cargando plantilla...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen mesh-gradient" data-testid="direct-template-page">
      {/* Header */}
      <header className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link 
              to="/plantillas" 
              className="flex items-center space-x-2 text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Plantillas</span>
            </Link>
            <div className="flex items-center space-x-3">
              <FileCheck className="w-6 h-6 text-blue-500" />
              <span className="font-semibold font-['Outfit']">Completar Plantilla</span>
            </div>
            <div className="w-20"></div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm mb-4">
            <Sparkles className="w-4 h-4" />
            <span>Llenado Directo</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] mb-2">
            {templateDef.title}
          </h1>
          <p className="text-slate-400 max-w-xl mx-auto">
            {templateDef.description}
          </p>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-8 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-300 font-medium">Llenado directo de plantilla</p>
            <p className="text-sm text-slate-400 mt-1">
              Complete los campos a continuación. Los campos marcados con * son obligatorios.
              Una vez completado, se generará su documento automáticamente.
            </p>
          </div>
        </div>

        {/* Form Sections */}
        <div className="space-y-6">
          {templateDef.sections.map((section, sectionIdx) => (
            <div 
              key={sectionIdx}
              className="bg-card border border-border rounded-xl overflow-hidden"
            >
              <button
                onClick={() => toggleSection(sectionIdx)}
                className="w-full flex items-center justify-between p-4 bg-slate-800/50 hover:bg-slate-800 transition-colors"
              >
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center text-sm">
                    {sectionIdx + 1}
                  </span>
                  {section.name}
                </h3>
                {expandedSections[sectionIdx] ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </button>
              
              {expandedSections[sectionIdx] && (
                <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {section.fields.map(field => renderField(field))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate('/plantillas')}
            className="px-6 py-3 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Cancelar
          </button>
          <button
            onClick={generateContract}
            disabled={generating}
            className="px-8 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="generate-contract-btn"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Generando...
              </>
            ) : (
              <>
                <FileCheck className="w-5 h-5" />
                Generar Documento
              </>
            )}
          </button>
        </div>

        {/* Generated Contract Preview */}
        {generatedContract && (
          <div className="mt-8 bg-card border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                Documento Generado
              </h3>
              <div className="flex gap-2">
                <button className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Vista Previa
                </button>
                <button className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-sm flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Descargar
                </button>
              </div>
            </div>
            <div className="bg-slate-900 rounded-lg p-4 max-h-96 overflow-y-auto">
              <pre className="text-sm text-slate-300 whitespace-pre-wrap">
                {generatedContract.content}
              </pre>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DirectTemplatePage;
